import React from 'react';
import { identity } from 'lodash';

import {
  always,
  ATTRIBUTE_LIMIT,
  EMPTY_PREFILL_ATTRIBUTES,
} from '@neptune/shared/attributes-domain';
import { convertRegexpSearchToFilter, useMemoizedValue } from '@neptune/shared/common-util';
import { ShortIdsByProject } from '@neptune/shared/entity-domain';
import {
  unwindSubpropertyStrategies,
  UnwindSubpropertyStrategy,
} from '@neptune/shared/entity-leaderboard-business-logic';
import { makeEntityIdentifier } from '@neptune/shared/entity-util';
import { queryAttributeDefinitionsPrioritized } from '@neptune/shared/leaderboard-domain';
import { regexpSearchFilter } from '@neptune/shared/search-business-logic';
import { FilterResult } from '@neptune/shared/search-domain';

import {
  AttributeDefinition,
  AttributeType,
  convertAttributeToSearchString,
} from 'domain/experiment/attribute';

import { uniqAttributeDefinitions } from './uniq-attribute-definitions';

function getProjectLimitedSearchSpace(
  shortIdsByProject: ShortIdsByProject,
  fullSearch: boolean,
): string[] {
  const projectIdentifiers = [...shortIdsByProject.keys()];

  if (fullSearch) {
    return projectIdentifiers;
  }

  const filteredProjectIdentifiers = projectIdentifiers.filter(
    (projectIdentifier) => !!shortIdsByProject.get(projectIdentifier)?.length,
  );

  return filteredProjectIdentifiers.length > 0 ? filteredProjectIdentifiers : projectIdentifiers;
}

export type AttributeDefinitionPrioritizedDataSourceBase<T> = {
  subpropertyStrategy?: UnwindSubpropertyStrategy;
  enhancer?: (input: AttributeDefinition) => T;
  prefillAttributes?: AttributeDefinition[];
  filterAttributeTypes?: AttributeType[];
  filter?: (input: T) => boolean;
  fullSearch?: boolean;
  limit?: number;
};

export type AttributeDefinitionPrioritizedDataSourceMultiProject<T> =
  AttributeDefinitionPrioritizedDataSourceBase<T> & {
    shortIdsByProject: ShortIdsByProject;
    projectIdentifier?: never;
    shortIds?: never;
  };

export type AttributeDefinitionPrioritizedDataSourceSingleProject<T> =
  AttributeDefinitionPrioritizedDataSourceBase<T> & {
    shortIds?: string[];
    projectIdentifier: string;
    shortIdsByProject?: never;
  };

export type AttributeDefinitionPrioritizedDataSourceParams<T> =
  | AttributeDefinitionPrioritizedDataSourceMultiProject<T>
  | AttributeDefinitionPrioritizedDataSourceSingleProject<T>;

export type AttributeDefinitionPrioritizedResult<
  T extends AttributeDefinition = AttributeDefinition,
> = (query: string) => Promise<FilterResult<T> & { priorityMatches: Map<string, boolean> }>;

export function createAttributeDefinitionPrioritizedDataSource<
  T extends AttributeDefinition = AttributeDefinition,
>({
  projectIdentifier,
  limit = ATTRIBUTE_LIMIT,
  filterAttributeTypes,
  shortIds,
  shortIdsByProject,
  subpropertyStrategy = UnwindSubpropertyStrategy.none,
  enhancer = identity,
  filter = always,
  fullSearch = false,
  prefillAttributes = EMPTY_PREFILL_ATTRIBUTES,
}: AttributeDefinitionPrioritizedDataSourceParams<T>): AttributeDefinitionPrioritizedResult<T> {
  const rawQuery = async (query: string) => {
    let projectIdentifiers: string[] = [];
    let experimentIds: string[] = [];

    if (shortIdsByProject) {
      projectIdentifiers = getProjectLimitedSearchSpace(shortIdsByProject, fullSearch);
      experimentIds = Array.from(shortIdsByProject.entries()).flatMap(([project, ids]) =>
        ids.map((id) => makeEntityIdentifier(project, id)),
      );
    }

    if (projectIdentifier) {
      projectIdentifiers = [projectIdentifier];
      experimentIds = shortIds?.map((id) => makeEntityIdentifier(projectIdentifier, id)) ?? [];
    }

    return queryAttributeDefinitionsPrioritized({
      projectIdentifiers,
      filterAttributeTypes,
      limit,
      attributeNameFilter: convertRegexpSearchToFilter(query),
      experimentIds,
      fullSearch,
    });
  };

  const prepareOutput = (collection: AttributeDefinition[], search: string) => {
    const collectionWithSubproperties =
      unwindSubpropertyStrategies[subpropertyStrategy](collection);

    const collectionSource = uniqAttributeDefinitions(
      prefillAttributes,
      collectionWithSubproperties,
    );

    const enhancedCollection = collectionSource.map(enhancer).filter(filter);

    const allResults = regexpSearchFilter(
      search,
      enhancedCollection,
      convertAttributeToSearchString,
    );

    return allResults;
  };

  return async (query: string) => {
    const { attributeDefinitions, priorityMatches, thereIsMore } = await rawQuery(query);

    const entries = prepareOutput(attributeDefinitions, query);

    prefillAttributes.forEach((attribute) => {
      priorityMatches.set(attribute.name, true);
    });

    return {
      entries,
      thereIsMore,
      priorityMatches,
    };
  };
}

/**
 * ⚠️ WARNING: This hook has a high performance cost. Use it with caution.
 * It was designed with the intention of being used in a fields picker context.
 *
 */

export function useAttributeDefinitionPrioritizedDataSource<T>(
  params: AttributeDefinitionPrioritizedDataSourceMultiProject<T>,
): AttributeDefinitionPrioritizedResult;

export function useAttributeDefinitionPrioritizedDataSource<T>(
  params: AttributeDefinitionPrioritizedDataSourceSingleProject<T>,
): AttributeDefinitionPrioritizedResult;

export function useAttributeDefinitionPrioritizedDataSource<
  T extends AttributeDefinition = AttributeDefinition,
>(
  params: AttributeDefinitionPrioritizedDataSourceParams<T>,
): AttributeDefinitionPrioritizedResult<T> {
  const memoizedParams = useMemoizedValue(params);

  return React.useCallback(
    async (query: string) => {
      return createAttributeDefinitionPrioritizedDataSource(memoizedParams)(query);
    },
    [memoizedParams],
  );
}
